import { useEffect, useState } from "react";
import { UserCheck } from "lucide-react";
import * as magazzinoApi from "../../api/magazzino";
import { OrdineMagazzino, ClienteCandidato } from "../../api/magazzino";
import { Cliente } from "../../api/clients";
import { getErrorMessage } from "../../api/client";
import { useToast } from "../../context/ToastContext";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { ClienteAutocomplete } from "../../components/ui/ClienteAutocomplete";

/**
 * Chi è il cliente di questo ordine, quando il sistema non ha saputo deciderlo.
 *
 * ## Perché serve una schermata
 *
 * Il match sulla P.IVA assegna il cliente solo quando i candidati sono
 * **esattamente uno**. Zero o più d'uno lasciano l'ordine `da_confermare`, e
 * fin qui quel caso non aveva una via d'uscita dall'interfaccia: si poteva solo
 * modificare l'ordine e cercare il cliente a mano, senza vedere *perché* era
 * rimasto in sospeso.
 *
 * ## Le due situazioni, che non sono la stessa
 *
 * - **Più candidati** — la P.IVA scritta corrisponde a due o più clienti in
 *   anagrafica, quasi sempre doppioni. Qui la scelta è fra quelli, e mostrarli
 *   con P.IVA e codice accanto è l'unico modo di distinguerli: i nomi si
 *   somigliano, è il motivo per cui sono ambigui.
 * - **Nessun candidato** — la P.IVA non corrisponde a niente. Non c'è nulla da
 *   scegliere, e l'unica strada è cercare in anagrafica. La ricerca libera resta
 *   disponibile anche nel primo caso: i candidati sono un suggerimento forte,
 *   non una gabbia.
 *
 * ## Che cosa scrive
 *
 * Una PATCH con `cliente_preassegnato_id`. Il backend da lì scrive
 * `stato_match_cliente = risolto_manualmente` — **non** `automatico`, che
 * direbbe il contrario di quel che è appena successo.
 */
export function RisolviCliente({
  ordine,
  onChiudi,
  onFatto,
}: {
  ordine: OrdineMagazzino;
  onChiudi: () => void;
  onFatto: () => void;
}) {
  const { showToast } = useToast();
  const [candidati, setCandidati] = useState<ClienteCandidato[]>([]);
  const [caricati, setCaricati] = useState(false);
  const [sceltoId, setSceltoId] = useState("");
  const [cercaTesto, setCercaTesto] = useState("");
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  useEffect(() => {
    magazzinoApi
      .listCandidati(ordine.id)
      .then(({ candidati: c }) => setCandidati(c))
      // Senza candidati resta la ricerca libera, che è comunque la strada del
      // caso "nessuno": un errore qui non deve chiudere l'unica via d'uscita.
      .catch(() => setCandidati([]))
      .finally(() => setCaricati(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function salva(e: React.FormEvent) {
    e.preventDefault();
    if (!sceltoId) {
      setErrore("Scegli un cliente.");
      return;
    }

    setErrore(null);
    setInCorso(true);
    try {
      await magazzinoApi.aggiornaOrdine(ordine.id, { cliente_preassegnato_id: sceltoId });
      showToast("Cliente assegnato a mano.", "success");
      onFatto();
      onChiudi();
    } catch (err) {
      setErrore(getErrorMessage(err, "Impossibile assegnare il cliente."));
    } finally {
      setInCorso(false);
    }
  }

  return (
    <Modal
      open
      onClose={onChiudi}
      title="Chi è il cliente di questo ordine"
      size="md"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onChiudi}>
            Annulla
          </Button>
          <Button type="submit" form="form-risolvi-cliente" loading={inCorso} disabled={!sceltoId}>
            <UserCheck className="h-4 w-4" aria-hidden="true" />
            Assegna
          </Button>
        </div>
      }
    >
      <form id="form-risolvi-cliente" onSubmit={salva} className="flex flex-col gap-4" noValidate>
        <div className="rounded-lg border border-line bg-line/10 p-3 text-sm text-ink/70">
          <p>
            Sull&apos;ordine è scritto{" "}
            <span className="font-semibold text-ink">{ordine.cliente_nuovo_nome ?? "nessun nome"}</span>
            {ordine.cliente_nuovo_piva && (
              <>
                , P.IVA <span className="font-semibold text-ink">{ordine.cliente_nuovo_piva}</span>
              </>
            )}
            .
          </p>
        </div>

        {!caricati ? (
          <p className="text-sm text-ink/50">Cerco i candidati…</p>
        ) : candidati.length > 0 ? (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-medium text-ink">
              {candidati.length === 1
                ? "Un cliente ha questa partita IVA"
                : `${candidati.length} clienti hanno questa partita IVA`}
            </legend>
            {/* P.IVA e codice accanto al nome: i candidati sono ambigui proprio
                perché i nomi si somigliano, e senza gli altri due dati la
                scelta sarebbe a caso. */}
            {candidati.map((c) => (
              <label
                key={c.id}
                className="flex cursor-pointer items-start gap-2 rounded-lg border border-line bg-white p-2.5 text-sm"
              >
                <input
                  type="radio"
                  name="candidato"
                  value={c.id}
                  checked={sceltoId === c.id}
                  onChange={() => {
                    setSceltoId(c.id);
                    setCercaTesto("");
                    setErrore(null);
                  }}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                />
                <span className="min-w-0">
                  <span className="block font-medium text-ink">{c.nome}</span>
                  <span className="block text-xs text-ink/50">
                    {c.partita_iva ?? "senza P.IVA"}
                    {c.codice_cliente && ` · ${c.codice_cliente}`}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>
        ) : (
          <p className="text-sm text-ink/70">
            Nessun cliente in anagrafica ha questa partita IVA. Cercalo per nome qui sotto, oppure registralo prima in
            Anagrafiche.
          </p>
        )}

        <div className="flex flex-col gap-1.5">
          <ClienteAutocomplete
            label={candidati.length > 0 ? "Oppure cercane un altro" : "Cerca in anagrafica"}
            value={cercaTesto}
            onChange={(v) => {
              setCercaTesto(v);
              // Scrivere a mano annulla la scelta precedente: senza, resterebbe
              // selezionato un cliente che nel campo non si legge più.
              setSceltoId("");
            }}
            onSelect={(c: Cliente) => {
              setCercaTesto(c.nome);
              setSceltoId(c.id);
              setErrore(null);
            }}
          />
        </div>

        <p className="text-xs text-ink/45">
          L&apos;ordine risulterà «assegnato a mano», distinto da quelli agganciati dal sistema: serve a sapere quanto
          lavoro manuale sta chiedendo il confronto sulle partite IVA.
        </p>

        {errore && <p className="text-sm font-medium text-accent">{errore}</p>}
      </form>
    </Modal>
  );
}
